import { safeParseHTML } from './html-utility';
import { flatMap } from 'lodash';
import { VIZ_SELECTORS } from '@mode/shared/contract-common';

/**
 * Get viz token from full id string.
 *
 * Helper to parse the token out of the id string that includes the type.
 */
export function tokenFromVizID(id: string): string {
  return id.split('_')[1] || id;
}

/**
 * Parse layout string and return body elements.
 */
export function getBodyElementsFromLayout(layout: string): Element[] {
  return safeParseHTML(layout).body;
}

/**
 * Extract all mode elements from an array of elements.
 */
export function extractVizElements(elements: Element[]): Element[] {
  return flatMap(elements.map((e) => Array.from(e.querySelectorAll(VIZ_SELECTORS))));
}

/**
 * Get only chart tokens from an array of elements.
 */
export function getChartTokensFromElements(elements: Element[]): string[] {
  return elements.filter((e) => e.tagName === 'MODE-CHART').map((e) => tokenFromVizID(e.id));
}

/**
 * Get only table tokens from an array of elements.
 */
export function getTableTokensFromElements(elements: Element[]) {
  return elements.filter((e) => e.tagName === 'MODE-TABLE').map((e) => tokenFromVizID(e.id));
}

export interface LayoutColumn {
  height: string;
  width: number;
  vizToken?: string;
}

export interface LayoutRow {
  height: string | null;
  columns: LayoutColumn[];
}

export interface LayoutGrid {
  rows: LayoutRow[];
}

export function getLayoutGridFromElements(elements: Element[]): LayoutGrid[] {
  const layoutGrid: LayoutGrid[] = [];
  const grids = elements.filter((e) => e.className.includes('mode-grid'));

  grids.forEach((grid) => {
    const resultGrid: LayoutGrid = { rows: [] };

    // iterate over every row and build LayoutRow
    Array.from(grid.getElementsByClassName('row')).forEach((row) => {
      let resultRow: LayoutRow = {
        height: row.getAttribute('data-row-height'),
        columns: [],
      };
      Array.from(row.children)
        .filter((e) => e.className.match(/col-(xs|sm|md|lg)-(\d{0,2})/))
        .forEach((e) => {
          const colInfo = e.className.match(/col-(xs|sm|md|lg)-(\d{0,2})/);
          const vizToken = tokenFromVizID(e.getElementsByTagName('mode-chart')[0]?.id || '');
          if (colInfo?.[1] && colInfo?.[2]) {
            resultRow.columns.push({
              height: colInfo[1],
              width: parseInt(colInfo[2]),
              vizToken,
            });
          }
        });
      resultGrid.rows.push(resultRow);
    });
    layoutGrid.push(resultGrid);
  });
  return layoutGrid;
}

/**
 * Return chart tokens for a given layout string.
 */
export function getChartTokensFromLayout(layout: string) {
  const bodyElements = getBodyElementsFromLayout(layout);
  const vizElementsInLayout = extractVizElements(bodyElements);
  return getChartTokensFromElements(vizElementsInLayout);
}

export function getLayoutGridWithVizTokens(layout: string) {
  const bodyElements = getBodyElementsFromLayout(layout);
  return getLayoutGridFromElements(bodyElements);
}

// For the new refactored embed apps. Will superceed the "mode.embed" app once complete.
const builderApp = 'embed.builder';
const viewerApp = 'embed.report';

const embedApp = 'mode.embed';

const reportApp = 'mode.report';
const editorApp = 'mode.editor';

/**
 * Returns true if we're on the editor main app, i.e. chart designer, table designer, etc.
 * @param body
 */
export function isDesignerMode(body: HTMLElement) {
  const appName = body.getAttribute('mode-app');
  return appName === editorApp;
}

/**
 * Returns true if we're inside the iframe on the report builder
 * @param body
 */
export function isBuilderMode(body: HTMLElement) {
  const appName = body.getAttribute('mode-app');
  return appName === builderApp || (appName === embedApp && body.className.indexOf('embed-preview') >= 0);
}

/**
 * Returns true if we're inside the iframe on the report view page.
 * @param body
 */
export function isReportMode(body: HTMLElement) {
  const appName = body.getAttribute('mode-app');
  return appName === viewerApp || (appName === embedApp && body.className.indexOf('embed-preview') < 0);
}

/**
 * Returns true if we're inside the lightbox container. This is somewhat of a heuristic because it
 * assumes if we're on the report app, then must be a lightbox since visualizations would normally be
 * displayed in the
 * @param body
 */
export function isLightboxMode(body: HTMLElement) {
  const appName = body.getAttribute('mode-app');
  return appName === reportApp;
}

/**
 * Returns true if we're running in a phantomjs context. Not exclusive with isReportMode.
 * @param body
 */

export function isPhantomMode($window: Window) {
  return $window['_phantom' as keyof Window] != null || $window['callPhantom' as keyof Window] != null;
}

export function isPicassoPDF(body: HTMLElement) {
  return body.classList.contains('picasso-pdf');
}
